/**
 * Protocol Banners Component
 * Handles banner clicks and selling plan updates
 */

class ProtocolBanners extends HTMLElement {
  constructor() {
    super();
    this.sellingPlans = {};
    this.cartData = null;
    this.isUpdating = false;
  }

  connectedCallback() {
    this.banners = this.querySelectorAll('[data-banner-index]');
    this.banners.forEach(banner => {
      banner.addEventListener('click', this.handleBannerClick.bind(this));
    });
    this._onCartUpdated = this._handleCartUpdated.bind(this);
    document.addEventListener('cart-data-updated', this._onCartUpdated);
  }

  async handleBannerClick(event) {
    if (this.isUpdating) return;

    // Ignore clicks on the learn more link
    if (event.target.closest('.protocol-banners__learn-more')) {
      return;
    }

    const banner = event.currentTarget;
    const bannerType = banner.dataset.bannerType;

    if (bannerType === 'checkout') {
      await this.handleCheckoutBanner();
      return;
    }

    if (bannerType === 'subscription') {
      await this.handleSubscriptionBanner();
    }
  }

  async handleCheckoutBanner() {
    this.isUpdating = true;
    this.showLoading();

    try {
      const cartData = this.cartData;
      if (!cartData) throw new Error('Cart data not available');

      const updates = [];
      cartData.items.forEach((item, index) => {
        if (item.properties?._gift_id) return;
          updates.push({ 
            line: index + 1, 
            quantity: item.quantity, 
            selling_plan: '' , 
            properties: { '_source': 'protocol cart' } }); 
      });

      if (updates.length > 0) {
        await this.batchUpdateCart(updates);
      }

      window.location.href = this._buildCheckoutUrl();
    } catch (error) {
      this.hideLoading();
      this.showError(error.message || 'Failed to update cart. Please try again.');
      this.isUpdating = false;
    }
  }

  async handleSubscriptionBanner() {
    this.isUpdating = true;
    this.showLoading();

    try {
      const cartData = this.cartData;
      if (!cartData) throw new Error('Cart data not available');

      // Update all non-gift line items as subscription
      const updates = [];
      let itemIndex = 0;
      for (const item of cartData.items) {
        if (!item.properties?._gift_id) {
          const sellingPlanId = this.getSellingPlanForItem(itemIndex + 1);
          if (sellingPlanId) {
            updates.push({
              line: itemIndex + 1,
              quantity: item.quantity,
              selling_plan: sellingPlanId,
              properties: { '_source': 'protocol cart' }
            });
          }
        }
        itemIndex++;
      }

      if (updates.length === 0) {
        window.location.href = this._buildCheckoutUrl();
        return;
      }

      // Batch update cart with selling plans (sequential calls to /cart/change.js)
      await this.batchUpdateCart(updates);

      window.location.href = this._buildCheckoutUrl();

    } catch (error) {
      this.hideLoading();
      this.showError(error.message || 'Failed to update cart. Please try again.');
      this.isUpdating = false;
    }
  }

  _handleCartUpdated(event) {
    const cartData = event.detail?.cartData;
    if (!cartData) return;
    this.cartData = cartData;
    this.sellingPlans = {};
    this._populateSellingPlans(cartData).catch(() => null);
  }

  async _populateSellingPlans(cartData) {
    const promises = cartData.items
      .map((item, index) => ({ item, line: index + 1 }))
      .filter(({ item }) => !item.properties?._gift_id)
      .map(({ item, line }) =>
        fetch(`/products/${item.handle}?view=get-selling-plan`)
          .then(r => r.ok ? r.text() : null)
          .then(text => {
            const id = text?.trim();
            if (id) this.sellingPlans[line] = id;
          })
          .catch(() => null)
      );
    await Promise.all(promises);
  }

  getSellingPlanForItem(line) {
    return this.sellingPlans[line] || null;
  }

  async batchUpdateCart(updates) {
    const routes = window.routes || {};
    const cartChangeUrl = routes.cart_change_url || `${window.Shopify?.routes?.root || '/'}cart/change.js`;
    const sectionsToRender = this.getSectionsToRender().map(section => section.section);
    const sectionsUrl = window.location.pathname;

    const fetchUpdate = async (update) => {
      const body = JSON.stringify({
        line: update.line,
        quantity: update.quantity,
        ...(update.selling_plan != null && { selling_plan: update.selling_plan }),
        ...(update.properties && { properties: update.properties }),
        sections: sectionsToRender,
        sections_url: sectionsUrl
      });

      const response = await fetch(cartChangeUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body
      });

      if (!response.ok) {
        const errorDetail = `Cart update failed — line: ${update.line}, status: ${response.status}`;
        if (window.protocolBannersDebug) console.error('[ProtocolBanners]', errorDetail, { update, response });
        throw new Error(errorDetail);
      }

      const parsedState = await response.json();
      if (parsedState.errors) {
        const errorDetail = `Cart update error — line: ${update.line}: ${parsedState.errors}`;
        if (window.protocolBannersDebug) console.error('[ProtocolBanners]', errorDetail, { update, parsedState });
        throw new Error(errorDetail);
      }
      return parsedState;
    };

    let lastResponse = null;

    for (const update of updates) {
      lastResponse = await fetchUpdate(update);
    }

    if (lastResponse?.sections) {
      this.updateSections(lastResponse);
    }

    return lastResponse;
  }

  getSectionsToRender() {
    const sectionId = this.dataset.sectionId;
    return [
      {
        id: `shopify-section-${sectionId}`,
        section: sectionId,
        selector: '.js-contents'
      },
      {
        id: 'protocol-cart-summary',
        section: 'protocol-cart-summary',
        selector: '.js-contents'
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section'
      }
    ];
  }

  updateSections(parsedState) {
    this.getSectionsToRender().forEach(section => {
      const element = document.getElementById(section.id);
      if (element && parsedState.sections[section.section]) {
        const selector = element.querySelector(section.selector);
        if (selector) {
          selector.innerHTML = this.getSectionInnerHTML(
            parsedState.sections[section.section],
            section.selector
          );
        }
      }
    });
  }

  getSectionInnerHTML(html, selector) {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');
    const element = doc.querySelector(selector);
    return element ? element.innerHTML : html;
  }

  _buildCheckoutUrl() {
    const parts = [];

    for (const part of window.location.search.slice(1).split('&')) {
      if (!part) continue;
      const eqIndex = part.indexOf('=');
      const decodedKey = decodeURIComponent(eqIndex !== -1 ? part.slice(0, eqIndex) : part);
      const decodedValue = eqIndex !== -1 ? decodeURIComponent(part.slice(eqIndex + 1).replace(/\+/g, ' ')) : '';

      // Pattern 1: individual checkout[key]=value params
      if (decodedKey.startsWith('checkout[')) {
        parts.push(decodedKey + '=' + encodeURIComponent(decodedValue));
        continue;
      }

      // Pattern 2: checkout={"email" => "...", "shipping_address" => {...}} Ruby hash
      if (decodedKey === 'checkout') {
        try {
          const obj = JSON.parse(decodedValue.replace(/ => /g, ': '));
          for (const [key, value] of Object.entries(obj)) {
            if (typeof value === 'object' && value !== null) {
              for (const [subKey, subValue] of Object.entries(value)) {
                parts.push('checkout[' + key + '][' + subKey + ']=' + encodeURIComponent(subValue));
              }
            } else {
              parts.push('checkout[' + key + ']=' + encodeURIComponent(value));
            }
          }
        } catch (e) {
          console.error(e);
        }
      }
    }

    return parts.length ? '/checkout?' + parts.join('&') : '/checkout';
  }

  disconnectedCallback() {
    document.removeEventListener('cart-data-updated', this._onCartUpdated);
  }

  showLoading() {
    document.body.classList.add('subscription-active');
  }

  hideLoading() {
    document.body.classList.remove('subscription-active');
  }

  showError(message) {
    // Create error notification
    const errorDiv = document.createElement('div');
    errorDiv.className = 'protocol-banners__error';
    errorDiv.style.cssText = `
      position: fixed;
      top: 20px;
      right: 20px;
      background: #dc2626;
      color: white;
      padding: 16px 24px;
      border-radius: 8px;
      box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
      z-index: 10000;
      max-width: 400px;
    `;
    errorDiv.textContent = message;
    document.body.appendChild(errorDiv);

    // Remove after 5 seconds
    setTimeout(() => {
      errorDiv.remove();
    }, 5000);
  }
}

export default ProtocolBanners;
